import { useEffect } from 'react';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import Axios from 'axios';

import {
  ACCESS_MANAGER_ROLE_ID,
  ADMINISTRATOR_SYSTEM_ROLE_ID,
  AGENT_SYSTEM_ROLE_ID,
  BASIC_SYSTEM_ROLE_ID,
  CUSTOMER_ADMIN_ROLE_ID,
  DEVELOPER_SYSTEM_ROLE_ID,
  GUEST_SYSTEM_ROLE_ID,
  INTEGRATION_SYSTEM_ROLE_ID,
  MANAGER_SYSTEM_ROLE_ID,
  NOTIFICATION_SYSTEM_ROLE_ID,
  POWER_USER_SYSTEM_ROLE_ID,
  READ_ONLY_ADMIN_SYSTEM_ROLE_ID,
  REVIEWER_SYSTEM_ROLE_ID,
  SPECIALIST_SYSTEM_ROLE_ID,
  SUPER_USER_SYSTEM_ROLE_ID,
  SUPPORT_ADMIN_ROLE_ID
} from '@attackiq/constants/ids';

import { Company } from '../companies';

import { CurrentUserCapabilities, getCurrentUserCapabilities } from './getCurrentUserCapabilities';
import { preloadUserAvatar } from './preloadUserAvatar';

import type { CustomerGroup } from '../mssp/useCustomerGroups';
import type { PinnedShortcutsByApp } from './useUsers';

export interface ManagedCustomer {
  display_name: string;
  id: string;
  name: string;
  subdomain: string;
  customeradmincompany__id?: string;
  salesadmincompany__id?: string;
}

export type AuthMethod = 'password' | 'sso' | 'linkedin' | 'shadow';

export interface CurrentUser {
  id: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  email: string;
  avatar: SizedImage;
  system_role: SystemRole;
  groups: string[];
  company: Company;
  last_project_id: null;
  is_locked: boolean;
  is_mfa_enabled: boolean;
  is_deleted: boolean;
  meta_data: {
    force_create_support_user: boolean;
    date_format_mask: string;
    time_format_mask: string;
    support_user_id: number;
  } | null;
  must_change_password: boolean;
  last_login: string;
  previous_last_login: string;
  login_attempts: number;
  pinned_shortcuts: PinnedShortcutsByApp | null;
  token: string;
  trial: boolean;
  disable_all_telemetry: number;
  subdomains_enabled: boolean;
  features: { [key: string]: boolean };
  last_eula_accepted: boolean;
  custom_disclaimer: { id: string } | null;
  is_offline_no_smtp: boolean;
  smtp_enabled: boolean;
  use_heap_io: boolean;
  use_hotjar: boolean;
  use_new_firedrill_search: boolean;
  is_prod: boolean;
  password_expiration_enabled: boolean;
  title: string;
  is_partner: boolean;
  allow_shadow_login: boolean;
  salesforce_enabled: boolean;
  managed_customers?: ManagedCustomer[];
  business_units: CustomerGroup[];
  on_behalf_of_user_email: string;
  /**
   * How this session authenticated (AP-21945), or null for a session opened before the marker
   * existed. Reflects the credential actually used, not what the company has configured. Useful for
   * support and QA; do not gate MFA on it directly, use `mfa_gates_apply`.
   */
  auth_method: AuthMethod | null;
  /**
   * Whether the MFA gates apply to this session, as MfaMiddleware decides it. The server owns this
   * rule so the UI cannot drift from it, which is exactly how AP-21945 went wrong.
   */
  mfa_gates_apply: boolean;
  capabilities?: string[];
  /**
   * Per-partner product rebrands. Always present on the response, but nullable: the
   * `company.has_partner` branch assigns `CompanyPartner.<x>_product_name` unguarded and that
   * column is `null=True` (ai_cloud/models.py:845), so a partner that never set a rebrand sends
   * `null` rather than the platform default.
   */
  ready_product_name: string | null;
  flex_product_name: string | null;
  enterprise_product_name: string | null;
  deepsurface_product_name: string | null;
}

type SystemRole =
  | { id: typeof SUPPORT_ADMIN_ROLE_ID; name: 'Notification'; value: -104 }
  | { id: typeof NOTIFICATION_SYSTEM_ROLE_ID; name: 'Notification'; value: -103 }
  | { id: typeof INTEGRATION_SYSTEM_ROLE_ID; name: 'Integration'; value: -102 }
  | { id: typeof ACCESS_MANAGER_ROLE_ID; name: 'User Manager'; value: -3 }
  | { id: typeof REVIEWER_SYSTEM_ROLE_ID; name: 'Mission Control Reviewer'; value: -2 }
  | { id: typeof SPECIALIST_SYSTEM_ROLE_ID; name: 'Mission Control Specialist'; value: -1 }
  | { id: typeof AGENT_SYSTEM_ROLE_ID; name: 'Agent'; value: 0 }
  | { id: typeof GUEST_SYSTEM_ROLE_ID; name: 'Guest'; value: 10 }
  | { id: typeof READ_ONLY_ADMIN_SYSTEM_ROLE_ID; name: 'Read Only Admin'; value: 15 }
  | { id: typeof BASIC_SYSTEM_ROLE_ID; name: 'Basic'; value: 20 }
  | { id: typeof DEVELOPER_SYSTEM_ROLE_ID; name: 'Developer'; value: 30 }
  | { id: typeof POWER_USER_SYSTEM_ROLE_ID; name: 'PowerUser'; value: 40 }
  | { id: typeof MANAGER_SYSTEM_ROLE_ID; name: 'Manager'; value: 50 }
  | { id: typeof CUSTOMER_ADMIN_ROLE_ID; name: 'CustomerAdmin'; value: 60 }
  | { id: typeof ADMINISTRATOR_SYSTEM_ROLE_ID; name: 'Administrator'; value: 70 }
  | { id: typeof SUPER_USER_SYSTEM_ROLE_ID; name: 'SuperUser'; value: 80 };

export const currentUserQueryOptions = queryOptions({
  queryKey: ['/v1/users/current_user'],
  queryFn: async ({ queryKey: [url] }) => {
    const { data } = await Axios.get<CurrentUser>(url);

    return data;
  },
  refetchInterval: 1000 * 60 * 5 // 5 minutes
});

export const useCurrentUser = () => {
  const queryClient = useQueryClient();

  const queryResult = useQuery(currentUserQueryOptions);

  useEffect(() => {
    if (!queryResult.data) return;

    const { id, first_name, last_name, avatar, is_active } = queryResult.data;

    preloadUserAvatar(queryClient, { avatar, first_name, id, is_active, last_name });
  }, [queryResult.data, queryClient]);

  return queryResult;
};

/** Whether a LaunchDarkly flag is on for this user's company. `false` while the user is loading. */
export const useFeature = (featureKey: string): boolean => {
  const { data: currentUser } = useCurrentUser();

  return !!currentUser?.features?.[featureKey];
};

export const useCurrentUserCapabilities = (): CurrentUserCapabilities => {
  const { data: currentUser } = useCurrentUser();

  return getCurrentUserCapabilities(currentUser);
};
